// Component: BacktestPerformance.jsx
// Purpose: Categorized metrics dashboard v2 — Profitability, Trade Stats, Risk, Streaks, Risk-Adjusted Returns
import React from 'react';
import { FiTrendingUp, FiBarChart2, FiShield, FiZap, FiActivity, FiTarget } from 'react-icons/fi';
import { EQUITY_CURVE_METRICS } from '../../../../../utils/backtestMetricsCalc';

// --> Safe number extractor
const asNum = (v, fb = 0) => { const n = Number(v); return Number.isFinite(n) ? n : fb; };

// --> INR currency formatter
const fmtINR = (val) => {
    if (val === null || val === undefined || !Number.isFinite(Number(val))) return null;
    return `₹${Number(val).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

// --> Format date to DD-MM-YYYY
const fmtDate = (dateStr) => {
    if (!dateStr) return null;
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return null;
        return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    } catch { return null; }
};

/*>>>>>>>>>>>>>>>>>>>>>>  VISUAL INDICATORS  <<<<<<<<<<<<<<<<<<<<<<*/

// --> 5-dot rating system with type-specific thresholds
const getRating = (type, value) => {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return { filled: 0, color: '#6b7280' };
    const v = Number(value);

    if (type === 'sharpe' || type === 'sortino') {
        if (v < 0)   return { filled: 0, color: '#ef4444' };
        if (v < 0.5) return { filled: 1, color: '#f97316' };
        if (v < 1.0) return { filled: 2, color: '#eab308' };
        if (v < 2.0) return { filled: 3, color: '#22c55e' };
        if (v < 3.0) return { filled: 4, color: '#16a34a' };
        return { filled: 5, color: '#f59e0b' };
    }
    if (type === 'profit_factor') {
        if (v < 1.0) return { filled: 1, color: '#ef4444' };
        if (v < 1.5) return { filled: 2, color: '#f97316' };
        if (v < 2.0) return { filled: 3, color: '#22c55e' };
        if (v < 3.0) return { filled: 4, color: '#16a34a' };
        return { filled: 5, color: '#f59e0b' };
    }
    if (type === 'calmar') {
        if (v <= 0)  return { filled: 0, color: '#ef4444' };
        if (v < 0.5) return { filled: 1, color: '#f97316' };
        if (v < 1.0) return { filled: 2, color: '#eab308' };
        if (v < 2.0) return { filled: 3, color: '#22c55e' };
        if (v < 3.0) return { filled: 4, color: '#16a34a' };
        return { filled: 5, color: '#f59e0b' };
    }
    if (type === 'win_rate') {
        if (v < 20)  return { filled: 1, color: '#ef4444' };
        if (v < 35)  return { filled: 2, color: '#f97316' };
        if (v < 50)  return { filled: 3, color: '#eab308' };
        if (v < 65)  return { filled: 4, color: '#22c55e' };
        return { filled: 5, color: '#16a34a' };
    }
    if (type === 'ratio_wl') {
        if (v < 0.5) return { filled: 1, color: '#ef4444' };
        if (v < 1.0) return { filled: 2, color: '#f97316' };
        if (v < 1.5) return { filled: 3, color: '#eab308' };
        if (v < 2.5) return { filled: 4, color: '#22c55e' };
        return { filled: 5, color: '#16a34a' };
    }
    if (type === 'drawdown') {
        //! Drawdown: uniform red (#ef4444). Filled count = severity (more filled = worse risk).
        if (v < 2)   return { filled: 1, color: '#ef4444' };
        if (v < 5)   return { filled: 2, color: '#ef4444' };
        if (v < 10)  return { filled: 3, color: '#ef4444' };
        if (v < 20)  return { filled: 4, color: '#ef4444' };
        return { filled: 5, color: '#ef4444' };
    }
    if (type === 'expectancy') {
        if (v < -100)  return { filled: 0, color: '#ef4444' };
        if (v < 0)     return { filled: 1, color: '#f97316' };
        if (v < 50)    return { filled: 2, color: '#eab308' };
        if (v < 150)   return { filled: 3, color: '#22c55e' };
        if (v < 300)   return { filled: 4, color: '#16a34a' };
        return { filled: 5, color: '#f59e0b' };
    }
    return { filled: 0, color: '#6b7280' };
};

// --> 5-dot rating indicator
const Dots = ({ type, value }) => {
    const { filled, color } = getRating(type, value);
    return (
        <span className="inline-flex items-center gap-[3px] ml-2.5 shrink-0">
            {[1, 2, 3, 4, 5].map(i => (
                <span
                    key={i}
                    className="block rounded-full transition-colors"
                    style={{
                        width: 7, height: 7,
                        backgroundColor: i <= filled ? color : 'transparent',
                        border: `1.5px solid ${i <= filled ? color : 'var(--dot-border, #4b5563)'}`,
                    }}
                />
            ))}
        </span>
    );
};

// --> Compact inline proportional bar
const InlineBar = ({ value, max, color = '#10b981' }) => {
    const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    return (
        <span className="inline-flex items-center ml-2.5 shrink-0" style={{ width: 56 }}>
            <span className="block w-full h-[5px] rounded-full overflow-hidden bg-bg-3">
                <span className="block h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
            </span>
        </span>
    );
};

// --> Win rate arc (inline SVG ring)
const WinArc = ({ pct }) => {
    const r = 10;
    const circ = 2 * Math.PI * r;
    const v = Math.min(Math.max(asNum(pct), 0), 100);
    const dash = (v / 100) * circ;
    const { color } = getRating('win_rate', v);
    return (
        <svg width="28" height="28" viewBox="0 0 28 28" className="shrink-0 ml-2">
            <circle cx="14" cy="14" r={r} fill="none" strokeWidth="3" className="stroke-gray-200 dark:stroke-gray-700" />
            <circle
                cx="14" cy="14" r={r} fill="none" strokeWidth="3"
                stroke={color}
                strokeDasharray={`${dash} ${circ}`}
                strokeLinecap="round"
                transform="rotate(-90 14 14)"
            />
        </svg>
    );
};

// --> DD Timeline (compact inline)
const DDTimelineInline = ({ startDate, endDate, ddStart, ddEnd }) => {
    if (!startDate || !endDate || !ddStart) return null;
    try {
        const ps = new Date(startDate).getTime();
        const pe = new Date(endDate).getTime();
        const ds = new Date(ddStart).getTime();
        const de = ddEnd ? new Date(ddEnd).getTime() : pe;
        const total = pe - ps;
        if (total <= 0) return null;
        const left = Math.max(0, Math.min(((ds - ps) / total) * 100, 100));
        const width = Math.max(2, Math.min(((de - ds) / total) * 100, 100 - left));
        return (
            <span className="inline-flex items-center ml-2.5 shrink-0" style={{ width: 80 }}>
                <span className="relative block w-full h-[5px] rounded-full overflow-hidden bg-bg-3">
                    <span
                        className="absolute h-full rounded-full"
                        style={{ left: `${left}%`, width: `${width}%`, backgroundColor: '#ef4444' }}
                    />
                </span>
            </span>
        );
    } catch { return null; }
};

// --> N/A badge for equity-curve-only metrics when side filter is active
const NA = ({ reason = 'Available in "All" view only' }) => (
    <span className="text-[11px] text-fg-3 font-mono" title={reason}>N/A</span>
);

/*>>>>>>>>>>>>>>>>>>>>>>  LAYOUT PRIMITIVES  <<<<<<<<<<<<<<<<<<<<<<*/

// --> Uniform metric row: Label ... Value [Visual]
const Row = ({ label, children, last = false }) => (
    <div className={`flex items-center justify-between py-[7px] min-h-[34px] ${last ? '' : 'border-b border-[var(--divider)]'}`}>
        <span className="text-[11px] text-fg-2 shrink-0 pr-3 leading-tight">{label}</span>
        <div className="flex items-center justify-end shrink-0 gap-0.5">
            {children}
        </div>
    </div>
);

// --> Consistent value formatting
const Val = ({ children, color = 'text-fg-1', mono = true }) => (
    <span className={`text-[11px] font-semibold leading-tight ${mono ? 'font-mono' : ''} ${color}`}>{children}</span>
);

// --> Badge for return %, outperform/underperform — text only, no background
const Badge = ({ children, positive }) => (
    <span className={`text-[10px] font-semibold leading-tight ml-1.5 ${
        positive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
    }`}>
        {children}
    </span>
);

/*>>>>>>>>>>>>>>>>>>>>>>  CATEGORY CARD  <<<<<<<<<<<<<<<<<<<<<<*/

const ACCENTS = {
    emerald: { border: 'border-l-emerald-500', iconBg: 'bg-emerald-50 dark:bg-emerald-500/10', iconText: 'text-emerald-600 dark:text-emerald-400' },
    blue:    { border: 'border-l-blue-500',    iconBg: 'bg-blue-50 dark:bg-blue-500/10',    iconText: 'text-blue-600 dark:text-blue-400' },
    rose:    { border: 'border-l-rose-500',    iconBg: 'bg-rose-50 dark:bg-rose-500/10',    iconText: 'text-rose-600 dark:text-rose-400' },
    amber:   { border: 'border-l-amber-500',   iconBg: 'bg-amber-50 dark:bg-amber-500/10',  iconText: 'text-amber-600 dark:text-amber-400' },
    indigo:  { border: 'border-l-indigo-500',  iconBg: 'bg-indigo-50 dark:bg-indigo-500/10', iconText: 'text-indigo-600 dark:text-indigo-400' },
};

const Card = ({ icon: Icon, title, accent = 'blue', children }) => {
    const a = ACCENTS[accent];
    return (
        <div className={`bg-bg-2 rounded-xl border border-[var(--divider)] border-l-[3px] ${a.border} shadow-sm overflow-hidden`}>
            {/* Card header */}
            <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-[var(--divider)] dark:border-[var(--divider)] bg-bg-3/50 dark:bg-bg-2/20">
                {Icon && (
                    <span className={`p-1.5 rounded-lg ${a.iconBg}`}>
                        <Icon size={13} className={a.iconText} />
                    </span>
                )}
                <span className="ds-eyebrow text-fg-2">{title}</span>
            </div>
            {/* Card body */}
            <div className="px-4 py-1.5">
                {children}
            </div>
        </div>
    );
};

/*>>>>>>>>>>>>>>>>>>>>>>  HERO SUMMARY BAR  <<<<<<<<<<<<<<<<<<<<<<*/

const HeroStat = ({ label, value, sub, positive, icon: Icon, iconColor }) => (
    <div className="flex items-center gap-2.5 min-w-0 bg-bg-2/40 rounded-lg px-3 py-2.5 border border-[var(--divider)]">
        {Icon && (
            <Icon size={14} className={`shrink-0 opacity-60 ${iconColor}`} />
        )}
        <div className="min-w-0">
            <p className="text-[9px] font-bold text-fg-3 uppercase tracking-widest mb-0.5">{label}</p>
            <p className={`text-[13px] font-bold font-mono leading-tight truncate ${
                positive === true ? 'text-emerald-600 dark:text-emerald-400' :
                positive === false ? 'text-red-600 dark:text-red-400' :
                'text-fg-1'
            }`}>
                {value}
            </p>
            {sub && <p className="text-[10px] text-fg-3 font-mono mt-0.5">{sub}</p>}
        </div>
    </div>
);

/*>>>>>>>>>>>>>>>>>>>>>>  MAIN COMPONENT  <<<<<<<<<<<<<<<<<<<<<<*/

const BacktestPerformance = ({ metrics, trades, initialCapital, startDate, endDate, sideFilter }) => {
    if (!metrics) {
        return (
            <div className="flex items-center justify-center h-40 text-sm text-fg-3">
                No performance data available.
            </div>
        );
    }

    const isNA = (key) => EQUITY_CURVE_METRICS.has(key) && (metrics[key] === null || metrics[key] === undefined);

    const returnPct = asNum(metrics.total_return_pct ?? metrics.totalReturnPct);
    const netProfit = asNum(metrics.net_profit ?? metrics.netProfit);
    //--> R2-F2: gross comes from the API; null (Unresolved) is never coerced to 0.
    const grossProfitRaw = metrics.gross_profit ?? metrics.grossProfit;
    const grossLossRaw = metrics.gross_loss ?? metrics.grossLoss;
    const grossProfit = grossProfitRaw === null || grossProfitRaw === undefined ? null : asNum(grossProfitRaw);
    const grossLoss = grossLossRaw === null || grossLossRaw === undefined ? null : asNum(grossLossRaw);
    const gpglMax = Math.max(grossProfit ?? 0, grossLoss ?? 0, 1);

    const bhInr = metrics.buy_hold_return_inr;
    const bhPct = metrics.buy_hold_return;

    const winRate = asNum(metrics.win_rate ?? metrics.winRate);
    const totalTrades = asNum(metrics.total_trades ?? metrics.totalTrades);
    const maxDd = metrics.max_drawdown;
    const ddStart = metrics.max_dd_start;
    const ddEnd = metrics.max_dd_end;
    const ddDuration = metrics.max_dd_duration_days;
    const ddRecovered = metrics.max_dd_recovered;

    const maxWins = asNum(metrics.max_consecutive_wins);
    const maxLosses = asNum(metrics.max_consecutive_losses);
    const streakMax = Math.max(maxWins, maxLosses, 1);

    const sharpe = metrics.sharpe_ratio;
    const sortino = metrics.sortino_ratio;
    const pf = metrics.profit_factor;

    return (
        <div className="flex flex-col gap-3 p-3">

            {/*>>> HERO SUMMARY BAR — subtle, blends into background <<<*/}
            <div className="px-1 pb-1">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <HeroStat
                        label="Net Profit"
                        icon={FiTrendingUp}
                        iconColor={netProfit >= 0 ? 'text-emerald-500' : 'text-red-500'}
                        value={`${netProfit >= 0 ? '+' : ''}${fmtINR(netProfit) ?? '₹0'}`}
                        sub={returnPct !== 0 ? `${returnPct >= 0 ? '+' : ''}${returnPct.toFixed(2)}%` : null}
                        positive={netProfit >= 0}
                    />
                    <HeroStat
                        label="Win Rate"
                        icon={FiTarget}
                        iconColor="text-blue-500"
                        value={`${winRate.toFixed(1)}%`}
                        sub={`${totalTrades} trades`}
                        positive={winRate >= 50 ? true : winRate >= 35 ? null : false}
                    />
                    <HeroStat
                        label="Max Drawdown"
                        icon={FiShield}
                        iconColor="text-rose-500"
                        value={maxDd != null ? `${Math.abs(asNum(maxDd)).toFixed(2)}%` : '—'}
                        sub={ddDuration != null ? `${ddDuration} days` : null}
                        positive={false}
                    />
                    <HeroStat
                        label="Sharpe Ratio"
                        icon={FiActivity}
                        iconColor="text-indigo-500"
                        value={sharpe != null ? asNum(sharpe).toFixed(2) : '—'}
                        sub={pf != null ? `PF: ${asNum(pf).toFixed(2)}` : null}
                        positive={sharpe != null ? (asNum(sharpe) >= 1 ? true : asNum(sharpe) >= 0 ? null : false) : null}
                    />
                </div>
            </div>

            {/*>>> DETAIL CARDS GRID <<<*/}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">

                {/*>>> PROFITABILITY <<<*/}
                <Card icon={FiTrendingUp} title="Profitability" accent="emerald">
                    <Row label="Period">
                        <Val color="text-fg-2" mono={false}>{startDate && endDate ? `${startDate} — ${endDate}` : '—'}</Val>
                    </Row>

                    <Row label="Net Profit">
                        <Val color={netProfit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>{fmtINR(netProfit) ?? '—'}</Val>
                        {returnPct !== 0 && <Badge positive={returnPct >= 0}>{returnPct >= 0 ? '+' : ''}{returnPct.toFixed(2)}%</Badge>}
                    </Row>

                    <Row label="Gross Profit">
                        <Val color="text-emerald-600 dark:text-emerald-400">{grossProfit === null ? 'Unresolved' : (fmtINR(grossProfit) ?? '—')}</Val>
                        <InlineBar value={grossProfit ?? 0} max={gpglMax} color="#10b981" />
                    </Row>

                    <Row label="Gross Loss">
                        <Val color="text-red-600 dark:text-red-400">{grossLoss === null ? 'Unresolved' : (fmtINR(grossLoss) ?? '—')}</Val>
                        <InlineBar value={grossLoss ?? 0} max={gpglMax} color="#ef4444" />
                    </Row>

                    <Row label="Expectancy">
                        <Val color={asNum(metrics.expectancy) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>{fmtINR(metrics.expectancy) ?? '—'}</Val>
                        <Dots type="expectancy" value={metrics.expectancy} />
                    </Row>

                    <Row label="Buy &amp; Hold Return">
                        {isNA('buy_hold_return_inr') ? <NA /> : (
                            <Val color="text-fg-2">
                                {bhInr != null ? fmtINR(bhInr) : '—'}
                                {bhPct != null && <span className="text-fg-3 ml-1">({asNum(bhPct).toFixed(2)}%)</span>}
                            </Val>
                        )}
                    </Row>

                    <Row label="vs Buy &amp; Hold" last>
                        {isNA('buy_hold_return') ? <NA /> : (() => {
                            if (bhPct === null || bhPct === undefined) return <Val color="text-fg-3">—</Val>;
                            const out = returnPct > asNum(bhPct);
                            return <Badge positive={out}>{out ? 'Outperforms' : 'Underperforms'}</Badge>;
                        })()}
                    </Row>
                </Card>

                {/*>>> TRADE STATISTICS <<<*/}
                <Card icon={FiBarChart2} title="Trade Statistics" accent="blue">
                    <Row label="Total Trades">
                        <Val>{String(totalTrades)}</Val>
                    </Row>

                    <Row label="Win Rate">
                        <Val color={winRate >= 50 ? 'text-emerald-600 dark:text-emerald-400' : winRate >= 35 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}>
                            {winRate.toFixed(1)}%
                        </Val>
                        <WinArc pct={winRate} />
                        <Dots type="win_rate" value={winRate} />
                    </Row>

                    <Row label="Avg P&amp;L">
                        <Val color={asNum(metrics.avg_pnl ?? metrics.avgPnl) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                            {fmtINR(metrics.avg_pnl ?? metrics.avgPnl) ?? '—'}
                        </Val>
                    </Row>

                    <Row label="Median P&amp;L">
                        <Val color={asNum(metrics.median_pnl ?? metrics.medianPnl) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                            {fmtINR(metrics.median_pnl ?? metrics.medianPnl) ?? '—'}
                        </Val>
                    </Row>

                    <Row label="Avg Win">
                        <Val color="text-emerald-600 dark:text-emerald-400">{fmtINR(metrics.avg_win ?? metrics.avgWin) ?? '—'}</Val>
                    </Row>

                    <Row label="Avg Loss">
                        <Val color="text-red-600 dark:text-red-400">{fmtINR(metrics.avg_loss ?? metrics.avgLoss) ?? '—'}</Val>
                    </Row>

                    <Row label="Ratio Avg Win/Loss">
                        {metrics.ratio_avg_win_loss != null ? (
                            <>
                                <Val>{asNum(metrics.ratio_avg_win_loss).toFixed(3)}</Val>
                                <Dots type="ratio_wl" value={metrics.ratio_avg_win_loss} />
                            </>
                        ) : <NA reason="No losses or insufficient data" />}
                    </Row>

                    <Row label="Largest Win">
                        <Val color="text-emerald-600 dark:text-emerald-400">{metrics.largest_win != null ? (fmtINR(metrics.largest_win) ?? '—') : '—'}</Val>
                    </Row>

                    <Row label="Largest Loss" last>
                        <Val color="text-red-600 dark:text-red-400">{metrics.largest_loss != null ? (fmtINR(metrics.largest_loss) ?? '—') : '—'}</Val>
                    </Row>
                </Card>

                {/*>>> RISK <<<*/}
                <Card icon={FiShield} title="Risk" accent="rose">
                    <Row label="Max Drawdown">
                        {isNA('max_drawdown') ? <NA /> : (
                            <>
                                <Val color="text-red-600 dark:text-red-400">{maxDd != null ? `${Math.abs(asNum(maxDd)).toFixed(2)}%` : '—'}</Val>
                                {maxDd != null && <Dots type="drawdown" value={Math.abs(asNum(maxDd))} />}
                            </>
                        )}
                    </Row>

                    <Row label="Drawdown Start">
                        {isNA('max_dd_start') ? <NA /> : <Val color="text-fg-2">{fmtDate(ddStart) ?? '—'}</Val>}
                    </Row>

                    <Row label="Drawdown End">
                        {isNA('max_dd_end') ? <NA /> : (
                            ddRecovered === false
                                ? <Val color="text-orange-600 dark:text-orange-400">Not recovered</Val>
                                : <Val color="text-fg-2">{fmtDate(ddEnd) ?? '—'}</Val>
                        )}
                    </Row>

                    <Row label="Duration">
                        {isNA('max_dd_duration_days') ? <NA /> : (
                            <Val color="text-fg-2">{ddDuration != null ? `${ddDuration} days` : '—'}</Val>
                        )}
                    </Row>

                    {!isNA('max_dd_start') && ddStart && (
                        <Row label="Drawdown Timeline">
                            <DDTimelineInline
                                startDate={startDate} endDate={endDate}
                                ddStart={ddStart} ddEnd={ddRecovered !== false ? ddEnd : endDate}
                            />
                        </Row>
                    )}

                    <Row label="Calmar Ratio" last>
                        {isNA('calmar_ratio') ? <NA /> : (
                            <>
                                <Val>{metrics.calmar_ratio != null ? asNum(metrics.calmar_ratio).toFixed(3) : '—'}</Val>
                                {metrics.calmar_ratio != null && <Dots type="calmar" value={metrics.calmar_ratio} />}
                            </>
                        )}
                    </Row>
                </Card>

                {/*>>> STREAKS <<<*/}
                <Card icon={FiZap} title="Streaks" accent="amber">
                    <Row label="Winning Streak">
                        <Val color="text-emerald-600 dark:text-emerald-400">{maxWins}</Val>
                        <InlineBar value={maxWins} max={streakMax} color="#10b981" />
                    </Row>

                    <Row label="Losing Streak" last>
                        <Val color="text-red-600 dark:text-red-400">{maxLosses}</Val>
                        <InlineBar value={maxLosses} max={streakMax} color="#ef4444" />
                    </Row>
                </Card>

                {/*>>> RISK-ADJUSTED RETURNS <<<*/}
                <Card icon={FiActivity} title="Risk-Adjusted Returns" accent="indigo">
                    <Row label="Sharpe Ratio">
                        {isNA('sharpe_ratio') ? <NA /> : (
                            <>
                                <Val>{sharpe != null ? asNum(sharpe).toFixed(3) : '—'}</Val>
                                {sharpe != null && <Dots type="sharpe" value={sharpe} />}
                            </>
                        )}
                    </Row>

                    <Row label="Sortino Ratio">
                        {isNA('sortino_ratio') ? <NA /> : (
                            <>
                                <Val>{sortino != null ? asNum(sortino).toFixed(3) : '—'}</Val>
                                {sortino != null && <Dots type="sortino" value={sortino} />}
                            </>
                        )}
                    </Row>

                    <Row label="Profit Factor" last>
                        <Val>{pf != null ? asNum(pf).toFixed(2) : '—'}</Val>
                        {pf != null && <Dots type="profit_factor" value={pf} />}
                    </Row>
                </Card>

            </div>
        </div>
    );
};

export default BacktestPerformance;
